import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import MainApi from "../../../../../../services/mainApi.service";
import { memo, useCallback, useMemo, useState } from "react";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { AllowedAction } from "./AllowedActionForm/AllowedAction";
import { ITicketAutomationReceivedRuleAllowedAction } from "../../../../../../models/ticket-aut-rec-rule-allowed-action";

const TICKET_AUTOMATION_RECEIVED_RULE_ALLOWED_ACTION_ROUTE =
  process.env.REACT_APP_AUTOMATIONS +
  "/api/v1/TicketAutomationReceivedRuleAllowedAction/";
const ticketAutomationReceivedRuleAllowedActionService =
  new MainApi<ITicketAutomationReceivedRuleAllowedAction>(
    TICKET_AUTOMATION_RECEIVED_RULE_ALLOWED_ACTION_ROUTE,
  );

const translationPath =
  "chatBot.page.automations.automationReceivedRules.ticketAutomationReceivedRuleAllowedAction.";

const AllowedActionPage = () => {
  const { t } = useTranslation();

  const { id: ticketAutomationReceivedRuleId } = useParams();

  const [classificationForm, setClassificationForm] = useState(false);

  const [showButton, setShowButton] = useState(false);

  const [selectedId, setSelectedId] = useState<string>();

  const columns: GridColDef<ITicketAutomationReceivedRuleAllowedAction>[] = [
    {
      field: "newSchedule",
      headerName: t(translationPath + "new_schedule") || "New Schedule",
      renderCell: (params) => {
        return (
          <Typography>
            {params.value
              ? t(translationPath + "yes")
              : t(translationPath + "no")}
          </Typography>
        );
      },
    },
    {
      field: "confirm",
      headerName: t(translationPath + "confirm_field") || "Confirm",
      renderCell: (params) => {
        return (
          <Typography>
            {params.value
              ? t(translationPath + "yes")
              : t(translationPath + "no")}
          </Typography>
        );
      },
    },
    {
      field: "cancel",
      headerName: t(translationPath + "cancel") || "Cancel",
      renderCell: (params) => {
        return (
          <Typography>
            {params.value
              ? t(translationPath + "yes")
              : t(translationPath + "no")}
          </Typography>
        );
      },
    },
    {
      field: "checkIn",
      headerName: t(translationPath + "check_in") || "Check-in",
      renderCell: (params) => {
        return (
          <Typography>
            {params.value
              ? t(translationPath + "yes")
              : t(translationPath + "no")}
          </Typography>
        );
      },
    },
    {
      field: "reschedule",
      headerName: t(translationPath + "reschedule") || "Reschedule",
      renderCell: (params) => {
        return (
          <Typography>
            {params.value
              ? t(translationPath + "yes")
              : t(translationPath + "no")}
          </Typography>
        );
      },
    },
    {
      field: "featuredTicketAutomationReceivedRule",
      headerName:
        t(translationPath + "featured_received_rule") ||
        "Featured Received Rule",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "returnTicketAutomationReceivedRule",
      headerName:
        t(translationPath + "return_received_rule") || "Return Received Rule",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "examTicketAutomationReceivedRule",
      headerName:
        t(translationPath + "exam_received_rule") || "Exam Received Rule",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "consultationTicketAutomationReceivedRule",
      headerName:
        t(translationPath + "consultation_received_rule") ||
        "Consultation Received Rule",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    setClassificationForm(true);
  };

  const handleRowClick = (
    params: GridRowParams<ITicketAutomationReceivedRuleAllowedAction>,
  ) => {
    if (!params.row) return;
    setClassificationForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = useCallback(() => {
    setSelectedId(undefined);
    setClassificationForm(false);
  }, []);

  const props = useMemo(() => {
    return {
      TicketAutomationReceivedRuleId: ticketAutomationReceivedRuleId,
    };
  }, [ticketAutomationReceivedRuleId]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {classificationForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <AllowedAction
            id={selectedId}
            ticketAutomationReceivedRuleId={ticketAutomationReceivedRuleId}
            onBackButton={handleBack}
          />
        </Box>
      )}
      {!classificationForm && (
        <Box height={600}>
          <Table<ITicketAutomationReceivedRuleAllowedAction>
            showDefaultMacroFilters={false}
            service={ticketAutomationReceivedRuleAllowedActionService}
            columns={columns}
            title={""}
            onFetchData={(items) => {
              setShowButton(items.length === 0);
            }}
            onFetchDelete={() => {
              setShowButton(true);
            }}
            showButton={showButton}
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputAutoFocus={false}
            defaultPageSize={20}
            serviceProps={props}
            pageSizeOptions={[5, 10, 20]}
            searchInputPlaceHolder={""}
            addButtonLabel={
              t(translationPath + "add_allowed_action") ||
              "Add Offer Classification"
            }
            confirmOptions={{
              title: t(translationPath + "confirm.title"),
              description: t(translationPath + "confirm.description"),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(AllowedActionPage);
